import { Box, Grid, autocompleteClasses } from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import { useDebounce } from "../hooks/use-debounce";
import { ViewType } from "../pages/app/provider/scheduling/SchedulingLayout";
// import { useProvidersList } from "../queries/provider/setting/use-providers-list";
import { useSearchPatientList } from "../queries/list/useSearchPatientList";
import AccordionFilters from "./AccordionFilters";
import MaskMultiSelectScroll from "@/components/MaskMultiSelectScroll";
import Text from "./Text";
import WeekPicker from "./WeekPicker";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";
import { PROVIDER_PATHS } from "../constants/paths";
import { useLocation } from "react-router-dom";
import { useAppointmentTypeGetAll } from "../queries/practice-setting/appointment-type-configuration/use-appoinment-type-get-all";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  DEFAULT_NEUTRAL_TEXT_COLOR,
  // DEFAULT_SECONDARY_TEXT_COLOR,
  BLUE_ACCENT_COLORS,
  DEFAULT_BORDER_COLOR,
  getXtenantId,
} from "../utils/utils";
import { useProviderListV2 } from "../queries/use-provider-list-v2";
import { useState } from "react";

interface SideFilterProps {
  selectedDate: any;
  patient: { value: string; label: string } | null;
  provider: { value: any; label: string }[] | undefined;
  filterOptions: any;
  setFilterOptions: any;
  view: ViewType;
  handleDateChange: any;
  setPatient: any;
  setProvider: any;
  showCalender: boolean;
  selectedAppointmentType: { value: any; label: string }[] | undefined;
  setSelectedAppointmentType: any;
}

const SideFilters = ({
  selectedDate,
  patient,
  provider,
  filterOptions,
  view,
  setFilterOptions,
  handleDateChange,
  setPatient,
  setProvider,
  showCalender,
  selectedAppointmentType,
  setSelectedAppointmentType,
}: SideFilterProps) => {
  const [searchPatientGroupKey, setSearchPatientGroupKey] =
    useDebounce<string>("");
  const [searchProviderGroupKey, setSearchProviderGroupKey] =
    useDebounce<string>("");
  const [patientPagination, setPatientPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [providerPagination, setProviderPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const location = useLocation();
  const isAvailability = location.pathname === PROVIDER_PATHS.AVAILABILITY;
  const { data: patientList, isFetching: patientListFetching } =
    useSearchPatientList({
      disabled: isAvailability,
      pagination: patientPagination,
      searchString: searchPatientGroupKey,
    });
  const { data: providersList, isFetching: providersListFetching } =
    useProviderListV2({
      pagination: providerPagination,
      searchString: searchProviderGroupKey,
      xTENANTID: getXtenantId(),
      isReferral: false,
    });

  const [searchAppointmentTypeKey, setSearchAppointmentTypeKey] =
    useDebounce<string>("");
  const [appointmentTypePagination, setAppointmentTypePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: appointmentTypeList, isFetching: appointmentTypeFetching } =
    useAppointmentTypeGetAll({
      pagination: appointmentTypePagination,
      searchString: searchAppointmentTypeKey,
      status: true,
      archive: false,
      xTENANTID: getXtenantId(),
    });

  //function to get the view list to show on calender
  const getViewList = (viewType: string): any[] =>
    viewType === "day" ? ["year", "month", "day"] : ["year", "month", "day"];

  return (
    <Box
      borderTop={"1px solid " + DEFAULT_BORDER_COLOR}
      height={"calc(100vh - 156px)"}
      sx={{ overflowY: "hidden" }}
    >
      <Grid
        container
        paddingLeft={"10px"}
        paddingTop={"8px"}
        paddingRight={"10px"}
        width={"100%"}
        mt={0}
      >
        {/* calendar is stick to the top */}
        <Grid item xs={12} md={12}>
          <Box
            width={"100%"}
            position={"relative"}
            display={"flex"}
            justifyContent={"center"}
            sx={{
              ".MuiMonthCalendar-root": {
                width: "100%",
                padding: 0,
                flexBasis: "5%",
              },
              ".MuiYearCalendar-root": {
                width: "100%",
                maxWidth: "100%",
                margin: "0 auto",
                padding: 0,
              },
              "& .MuiPickersCalendarHeader-label": {
                fontSize: "0.8rem",
                whiteSpace: "nowrap",
              },
              "& .MuiDayCalendar-weekDayLabel": {
                fontSize: "0.7rem",
                width: "28px",
                height: "28px",
                color: "#373D41",
              },
              "& .MuiPickersDay-root": {
                fontSize: "0.75rem",
                width: "28px",
                height: "28px",
                color: "#74797B",
                "&:hover": {
                  backgroundColor: "#F0F0F0",
                },
              },
              "& .MuiPickersDay-root.Mui-selected": {
                backgroundColor: "#104E84 !important",
                color: "#fff !important",
                border: "none !important",
                "&:hover": {
                  backgroundColor: "#0D3F6B !important",
                },
                "&:focus": {
                  backgroundColor: "#104E84 !important",
                },
              },
              "& .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected)": {
                backgroundColor: "#E8E8E8",
                color: "#373D41",
                border: "none !important",
                "&:hover": {
                  backgroundColor: "#DCDCDC",
                },
              },
              "& .MuiPickersDay-root.MuiPickersDay-today.Mui-selected": {
                backgroundColor: "#104E84 !important",
                color: "#fff !important",
                border: "none !important",
              },
            }}
          >
            {showCalender && view === "week" && (
              <Box
                sx={{
                  width: "100%",
                  overflow: "visible",
                  ".MuiDateCalendar-root": {
                    width: "100%",
                    minWidth: "unset",
                    height: "auto",
                  },
                  "& .MuiPickersCalendarHeader-root": {
                    color: "#21262B",
                  },
                  "& .MuiPickersCalendarHeader-label": {
                    color: "#21262B",
                  },
                  "& .MuiPickersDay-today:not(.Mui-selected)": {
                    backgroundColor:
                      BLUE_ACCENT_COLORS.backgroundColor + "!important",
                    color: BLUE_ACCENT_COLORS.textColor + "!important",
                  },
                  "& .MuiPickersArrowSwitcher-button": {
                    color: "#21262B",
                  },
                  "& .MuiDayCalendar-root": {
                    border: "1px solid #E7E7E7",
                    borderRadius: "4px",
                    paddingBottom: "4px",
                    marginBottom: "4px",
                  },
                  "& .MuiDayCalendar-slideTransition": {
                    minHeight: "192px",
                    overflow: "visible",
                  },
                  "& .MuiDayCalendar-weekDayLabel": {
                    color: "#373D41",
                  },
                }}
              >
                <WeekPicker
                  value={dayjs(selectedDate)}
                  onChange={(val: any) => {
                    handleDateChange(val);
                  }}
                />
              </Box>
            )}

            {showCalender && view !== "week" && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  // view={(view as any) ?? "day"}
                  views={getViewList(view as ViewType)}
                  value={dayjs(selectedDate)}
                  sx={{
                    tabSize: 1,
                    boxShadow: "none",
                    border: "none",
                    width: "100%",
                    minWidth: "unset",
                    height: "auto",
                    maxHeight: "none",
                    "& .MuiPickersCalendarHeader-root": {
                      color: "#21262B",
                    },
                    "& .MuiPickersCalendarHeader-label": {
                      color: "#21262B",
                    },
                    "& .MuiPickersArrowSwitcher-button": {
                      color: "#21262B",
                    },
                    "& .MuiDayCalendar-root": {
                      border: "1px solid #E7E7E7",
                      borderRadius: "3.5px",
                      paddingBottom: "4px",
                      marginBottom: "4px",
                    },
                    "& .MuiDayCalendar-slideTransition": {
                      // 6 rows × 28px day cell + row margins; prevents the
                      // last week (e.g. row containing only "31") from
                      // clipping out of the calendar box.
                      minHeight: "192px",
                      overflow: "visible",
                    },
                    "& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer":
                      {
                        justifyContent: "space-evenly",
                      },
                    "& .MuiYearCalendar-root": {
                      width: "100%",
                      maxWidth: "100%",
                      margin: "0 auto",
                    },
                  }}
                  onChange={(val) => {
                    handleDateChange(val);
                  }}
                />
              </LocalizationProvider>
            )}
          </Box>
        </Grid>
        <Box overflow={"auto"} height={"calc(100vh - 420px)"} width={"100%"}>
          <Grid item xs={12} md={12} mt={"12px"} mb={"12px"}>
            <Box
              sx={{
                [`& .${autocompleteClasses.popupIndicator}`]: {
                  transform: "none",
                },
                [`& .${autocompleteClasses.popupIndicator} .MuiSvgIcon-root`]: {
                  display: "none",
                },
                [`& .${autocompleteClasses.popupIndicator}::after`]: {
                  content: '""',
                  display: "block",
                  width: "20px",
                  height: "20px",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='20' width='20' viewBox='0 0 24 24'%3E%3Cpath d='M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z' fill='%23373D41'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                },
              }}
            >
              <Text
                mb={1}
                fontWeight={"medium"}
                color={DEFAULT_NEUTRAL_TEXT_COLOR}
              >
                Search By Patient
              </Text>
              <AsyncSelectScroll
                value={patient}
                placeholder="Search & Select"
                loading={patientListFetching}
                onSearch={(val) => {
                  setSearchPatientGroupKey(val);
                  setPatientPagination((p) => ({ ...p, page: 0 }));
                }}
                options={patientList?.data?.content?.map((it) => ({
                  value: it?.uuid,
                  label: it?.firstName + " " + it?.lastName,
                }))}
                onChange={(val) => {
                  setPatient(val);
                }}
                pagination={patientPagination}
                setPagination={setPatientPagination}
                totalElements={patientList?.data?.page?.totalElements}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={12} mb={"12px"}>
            <Box
              sx={{
                [`& .${autocompleteClasses.popupIndicator}`]: {
                  transform: "none",
                },
                [`& .${autocompleteClasses.popupIndicator} .MuiSvgIcon-root`]: {
                  display: "none",
                },
                [`& .${autocompleteClasses.popupIndicator}::after`]: {
                  content: '""',
                  display: "block",
                  width: "20px",
                  height: "20px",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='20' width='20' viewBox='0 0 24 24'%3E%3Cpath d='M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z' fill='%23373D41'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                },
              }}
            >
              <Text
                mb={1}
                fontWeight={"medium"}
                color={DEFAULT_NEUTRAL_TEXT_COLOR}
              >
                Search By Provider
              </Text>
              <MaskMultiSelectScroll
                value={provider}
                placeholder="Search & Select"
                loading={providersListFetching}
                options={providersList?.data?.content?.map((it) => ({
                  value: it?.uuid,
                  label: it?.name,
                }))}
                onSearch={(val: string) => {
                  setSearchProviderGroupKey(val);
                  setProviderPagination((p) => ({ ...p, page: 0 }));
                }}
                onChange={(val) => {
                  setProvider(val);
                }}
                pagination={providerPagination}
                setPagination={setProviderPagination}
                totalElements={providersList?.data?.page?.totalElements}
                // endIcon={<SearchIcon />}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={12} mb={"12px"}>
            <Box>
              <Text
                mb={1}
                fontWeight={"medium"}
                color={DEFAULT_NEUTRAL_TEXT_COLOR}
              >
                Search By Appointment Type
              </Text>
              <MaskMultiSelectScroll
                value={selectedAppointmentType}
                placeholder="Search & Select"
                loading={appointmentTypeFetching}
                options={appointmentTypeList?.data?.content?.map((it: any) => ({
                  value: it?.uuid,
                  label: it?.nameString,
                }))}
                onSearch={(val: string) => {
                  setSearchAppointmentTypeKey(val);
                  setAppointmentTypePagination((p) => ({ ...p, page: 0 }));
                }}
                onChange={(val) => {
                  setSelectedAppointmentType(val);
                }}
                pagination={appointmentTypePagination}
                setPagination={setAppointmentTypePagination}
                totalElements={appointmentTypeList?.data?.page?.totalElements}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={12} mb={"12px"}>
            <AccordionFilters
              onClear={(k) =>
                setFilterOptions((prev: any) => {
                  const copiedItem = cloneDeep(prev);
                  copiedItem[k].optList = copiedItem[k].optList?.map(
                    (it: any) => ({
                      ...it,
                      selected: false,
                    })
                  );
                  copiedItem[k].isSelectAll = false;
                  copiedItem[k].isExpand = false;
                  copiedItem[k].activeFilterCount = 0;

                  return copiedItem;
                })
              }
              onExpand={(k, stat) => {
                setFilterOptions((prev: any) => {
                  const copiedItem = cloneDeep(prev);
                  copiedItem[k].isExpand = stat;
                  return copiedItem;
                });
              }}
              data={filterOptions}
              onSelectAll={(k, checked) => {
                setFilterOptions((prev: any) => {
                  const copiedItem = cloneDeep(prev);
                  const isAllItemsSelected = copiedItem[k].optList?.every(
                    (it: any) => it.selected
                  );

                  copiedItem[k].optList = copiedItem[k].optList?.map(
                    (it: any) => ({
                      ...it,
                      selected: checked && !isAllItemsSelected,
                    })
                  );

                  copiedItem[k].isSelectAll = checked;

                  copiedItem[k].activeFilterCount = checked
                    ? copiedItem[k].optList?.length
                    : 0;

                  return copiedItem;
                });
              }}
              onChange={(k, stat, ind) => {
                setFilterOptions((prev: any) => {
                  const copiedItem = cloneDeep(prev);

                  const isAllItemsSelected = copiedItem[k].optList?.every(
                    (it: any, i: number) =>
                      !!stat && ((stat && it.selected) || i === ind)
                  );

                  copiedItem[k].optList[ind].selected = stat;
                  copiedItem[k].activeFilterCount =
                    copiedItem[k].activeFilterCount + (stat ? 1 : -1);
                  copiedItem[k].isSelectAll = isAllItemsSelected;
                  return copiedItem;
                });
              }}
            />
          </Grid>
        </Box>
      </Grid>
    </Box>
  );
};
export default SideFilters;
